import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet.markercluster";

import type { LeafletMarkerKind, LeafletMarkerModel, XGSLeafletMapProps } from "./xgsLeafletMap.types";
import "./xgsLeafletMap.scss";
import ReactDOMServer from 'react-dom/server';

const US_CENTER: [number, number] = [39.8283, -98.5795];

function markerHtml(m: LeafletMarkerModel): string {
  if (m.customSvg) {
    const sel = m.selected ? " xgs-leaflet-facility--selected" : "";

    let content: string;

    if (typeof m.customSvg === "string") {
      // Raw SVG string
      content = m.customSvg;
    } else {
      // React component with props
      content = ReactDOMServer.renderToStaticMarkup(
        React.createElement(m.customSvg.component, m.customSvg.props ?? {})
      );
    }

    return `<div class="xgs-leaflet-facility${sel}">${content}</div>`;
  }

  if (m.kind === "driver" && m.shapeSvgPath) {
    const fill = m.shapeColor || "#23356d";
    const sel = m.selected ? " xgs-leaflet-truck--selected" : "";
    return `<div class="xgs-leaflet-truck${sel}" aria-hidden="true"><svg width="36" height="36" viewBox="0 0 32 32" fill="${fill}"><path d="${m.shapeSvgPath}" /></svg></div>`;
  }

  if (m.kind === "delivery" || m.kind === "service") {
    const cls =
      m.kind === "delivery" ? "xgs-leaflet-delivery" : "xgs-leaflet-service";
    const sel = m.selected ? ` ${cls}--selected` : "";
    const bg = m.fillColor || "#828282";
    const lab = m.label ?? (m.kind === "service" ? "SC" : "·");
    return `<div class="${cls}${sel}" style="background:${bg}">${lab}</div>`;
  }

  const sel = m.selected ? " xgs-leaflet-pu--selected" : "";
  const fill = m.fillColor || "#E0E0E0";
  const border = m.borderColor && m.borderColor !== "transparent" ? m.borderColor : "transparent";
  return `<div class="xgs-leaflet-pu${sel}" style="background:${fill};border-color:${border}">PU</div>`;
}

function makeDivIcon(m: LeafletMarkerModel): L.DivIcon {
  return L.divIcon({
    className: "xgs-leaflet-div-icon",
    html: markerHtml(m),
    iconSize: m.kind === "driver" ? [40, 40] : m.kind === "pickup" ? [34, 34] : [30, 30],
    iconAnchor: m.kind === "driver" ? [20, 20] : m.kind === "pickup" ? [17, 17] : [15, 15],
    popupAnchor: [0, -12],
  });
}

function collectBoundsPoints(markers: LeafletMarkerModel[], polylines: XGSLeafletMapProps["polylines"]): L.LatLngExpression[] {
  const pts: L.LatLngExpression[] = markers.map((m) => [m.lat, m.lng]);
  (polylines || []).forEach((pl) => {
    pl?.positions?.forEach(([lat, lng]) => pts.push([lat, lng]));
  });
  return pts;
}

/**
 * Presentational Leaflet map: markers, optional polylines, clustering, popups via `renderPopup`.
 */
const XGSLeafletMap: React.FC<XGSLeafletMapProps> = ({
  className = "",
  defaultCenter = US_CENTER,
  defaultZoom = 4,
  markers,
  polylines = [],
  flyTo,
  onMarkerClick,
  renderPopup,
  overlay,
  useClustering = true,
  clusteringThreshold = 45,
  autoFit = true,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersLayerRef = useRef<L.Layer | null>(null);
  const polyLayerRef = useRef<L.LayerGroup | null>(null);
  const markerMetaRef = useRef<Map<L.Marker, { id: string; kind: LeafletMarkerKind }>>(new Map());
  const popupContainersRef = useRef<WeakMap<L.Marker, HTMLDivElement>>(new WeakMap());
  const hoverCloseTimerRef = useRef<WeakMap<L.Marker, number>>(new WeakMap());
  const markerHoverRef = useRef<WeakMap<L.Marker, boolean>>(new WeakMap());
  const popupHoverRef = useRef<WeakMap<L.Marker, boolean>>(new WeakMap());
  const popupHoverHandlersRef = useRef<
    WeakMap<L.Marker, { el: HTMLElement; onEnter: () => void; onLeave: () => void }>
  >(new WeakMap());
  const popupOpenReasonRef = useRef<WeakMap<L.Marker, "hover" | "click">>(new WeakMap());
  /** Marker whose popup is open — used to re-sync ReactDOM when `renderPopup` updates (e.g. form state). */
  const openPopupMarkerRef = useRef<L.Marker | null>(null);
  const renderPopupRef = useRef(renderPopup);
  const onMarkerClickRef = useRef(onMarkerClick);
  renderPopupRef.current = renderPopup;
  onMarkerClickRef.current = onMarkerClick;

  /** Re-render open popup when parent updates `renderPopup` (stateful selects / buttons). */
  useEffect(() => {
    const marker = openPopupMarkerRef.current;
    if (!marker) return;
    const m = marker as L.Marker & { isPopupOpen?: () => boolean };
    if (typeof m.isPopupOpen === "function" && !m.isPopupOpen()) return;
    const container = popupContainersRef.current.get(marker);
    const meta = markerMetaRef.current.get(marker);
    const rp = renderPopupRef.current;
    if (!container || !meta || !rp) return;
    const node = rp(meta.id, meta.kind);
    if (node != null) {
      ReactDOM.render(node as React.ReactElement, container);
    }
  }, [renderPopup]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || mapRef.current) return;

    const map = L.map(el, { zoomControl: true }).setView(defaultCenter, defaultZoom);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);

    mapRef.current = map;
    polyLayerRef.current = L.layerGroup().addTo(map);

    const ro = new ResizeObserver(() => {
      map.invalidateSize();
    });
    ro.observe(el);

    return () => {
      ro.disconnect();
      map.remove();
      mapRef.current = null;
      polyLayerRef.current = null;
      markersLayerRef.current = null;
      markerMetaRef.current = new Map();
    };
  }, [defaultCenter, defaultZoom]);

  useEffect(() => {
    const map = mapRef.current;
    const polyGroup = polyLayerRef.current;
    if (!map || !polyGroup) return;

    polyGroup.clearLayers();
    polylines.forEach((pl) => {
      if (!pl?.positions?.length) return;
      const polyline = L.polyline(pl.positions as L.LatLngExpression[], {
        color: pl.color ?? "#23356d",
        weight: pl.weight ?? 4,
        opacity: pl.opacity ?? 0.85,
      });

      if (pl.tooltip) {
        const content =
          typeof pl.tooltip === "string"
            ? pl.tooltip
            : ReactDOMServer.renderToStaticMarkup(pl.tooltip);  

        polyline.bindTooltip(content, {
          sticky: true,       
          opacity: 1,
          className: "xgs-leaflet-polyline-tooltip",
        });
      }

      polyline.addTo(polyGroup);
    });
  }, [polylines]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (markersLayerRef.current) {
      map.removeLayer(markersLayerRef.current);
      markersLayerRef.current = null;
    }

    markerMetaRef.current = new Map();

    const cluster =
      useClustering && markers.length >= clusteringThreshold
        ? (L as any).markerClusterGroup({
          chunkedLoading: true,
          maxClusterRadius: 52,
        })
        : null;

    const surface: L.FeatureGroup = cluster || L.featureGroup();

    markers.forEach((m) => {
      if (!Number.isFinite(m.lat) || !Number.isFinite(m.lng)) return;

      const marker = L.marker([m.lat, m.lng], {
        icon: makeDivIcon(m),
        zIndexOffset: m.selected ? 800 : m.kind === "driver" ? 400 : 0,
      });

      markerMetaRef.current.set(marker, { id: m.id, kind: m.kind });

      const cancelPendingClose = () => {
        const pending = hoverCloseTimerRef.current.get(marker);
        if (pending != null) {
          window.clearTimeout(pending);
          hoverCloseTimerRef.current.delete(marker);
        }
      };
      const scheduleHoverClose = () => {
        cancelPendingClose();
        const t = window.setTimeout(() => {
          const isMarkerHovered = markerHoverRef.current.get(marker) === true;
          const isPopupHovered = popupHoverRef.current.get(marker) === true;
          const openReason = popupOpenReasonRef.current.get(marker);
          const shouldKeepOpen = isMarkerHovered || isPopupHovered || openReason === "click";
          if (!shouldKeepOpen) {
            marker.closePopup();
            if (openPopupMarkerRef.current === marker) {
              openPopupMarkerRef.current = null;
            }
          }
        }, 160);
        hoverCloseTimerRef.current.set(marker, t);
      };

      const rp = renderPopupRef.current;
      if (rp) {
        const popupContainer = document.createElement("div");
        popupContainersRef.current.set(marker, popupContainer);
        // Prevent the map from capturing pointer events meant for popup controls (react-select, buttons).
        const domEvent = (L as typeof L & { DomEvent?: { disableClickPropagation: (el: HTMLElement) => void; disableScrollPropagation: (el: HTMLElement) => void } }).DomEvent;
        if (domEvent?.disableClickPropagation) {
          domEvent.disableClickPropagation(popupContainer);
        }
        if (domEvent?.disableScrollPropagation) {
          domEvent.disableScrollPropagation(popupContainer);
        }
        marker.bindPopup(popupContainer, {
          className: "xgs-leaflet-popup",
          maxWidth: 360,
          /** Map clicks should not steal focus / close popup while using embedded controls. */
          closeOnClick: false,
        });
        marker.on("popupopen", () => {
          openPopupMarkerRef.current = marker;
          cancelPendingClose();
          popupHoverRef.current.set(marker, false);
          const popupEl = marker.getPopup()?.getElement?.() ?? null;
          if (popupEl && domEvent?.disableClickPropagation) {
            domEvent.disableClickPropagation(popupEl);
          }
          if (popupEl && domEvent?.disableScrollPropagation) {
            domEvent.disableScrollPropagation(popupEl);
          }
          const prevHandlers = popupHoverHandlersRef.current.get(marker);
          if (prevHandlers) {
            prevHandlers.el.removeEventListener("mouseenter", prevHandlers.onEnter);
            prevHandlers.el.removeEventListener("mouseleave", prevHandlers.onLeave);
          }
          if (popupEl) {
            const onEnter = () => {
              popupHoverRef.current.set(marker, true);
              cancelPendingClose();
            };
            const onLeave = () => {
              popupHoverRef.current.set(marker, false);
              scheduleHoverClose();
            };
            popupEl.addEventListener("mouseenter", onEnter);
            popupEl.addEventListener("mouseleave", onLeave);
            popupHoverHandlersRef.current.set(marker, { el: popupEl, onEnter, onLeave });
          }
          const node = rp(m.id, m.kind);
          if (node != null) {
            ReactDOM.render(node as React.ReactElement, popupContainer);
          }
        });
        marker.on("popupclose", () => {
          cancelPendingClose();
          markerHoverRef.current.set(marker, false);
          popupHoverRef.current.set(marker, false);
          popupOpenReasonRef.current.delete(marker);
          const handlers = popupHoverHandlersRef.current.get(marker);
          if (handlers) {
            handlers.el.removeEventListener("mouseenter", handlers.onEnter);
            handlers.el.removeEventListener("mouseleave", handlers.onLeave);
            popupHoverHandlersRef.current.delete(marker);
          }
          if (openPopupMarkerRef.current === marker) {
            openPopupMarkerRef.current = null;
          }
          ReactDOM.unmountComponentAtNode(popupContainer);
        });
      }

      marker.on("mouseover", () => {
        markerHoverRef.current.set(marker, true);
        cancelPendingClose();
        if (popupOpenReasonRef.current.get(marker) !== "click") {
          popupOpenReasonRef.current.set(marker, "hover");
        }
        marker.openPopup();
      });
      marker.on("mouseout", () => {
        markerHoverRef.current.set(marker, false);
        scheduleHoverClose();
      });

      marker.on("click", () => {
        popupOpenReasonRef.current.set(marker, "click");
        cancelPendingClose();
        onMarkerClickRef.current?.(m.id, m.kind);
      });

      surface.addLayer(marker);
    });

    surface.addTo(map);
    markersLayerRef.current = surface;

    if (autoFit) {
      const boundsPts = collectBoundsPoints(markers, polylines);
      if (boundsPts.length === 1) {
        map.setView(boundsPts[0] as L.LatLngTuple, 13);
      } else if (boundsPts.length > 1) {
        map.fitBounds(L.latLngBounds(boundsPts as L.LatLngTuple[]), { padding: [28, 28], maxZoom: 15 });
      }
    }
  }, [markers, polylines, useClustering, clusteringThreshold, autoFit]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !flyTo || !Number.isFinite(flyTo.lat) || !Number.isFinite(flyTo.lng)) return;
    map.flyTo([flyTo.lat, flyTo.lng], flyTo.zoom ?? 15, { duration: 0.45 });
    // Intentionally depend on flyTo fields only (not object identity) to avoid animation loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flyTo?.lat, flyTo?.lng, flyTo?.zoom]);

  return (
    <div className={`xgs-leaflet-map ${className}`.trim()}>
      <div ref={containerRef} className="xgs-leaflet-map__canvas" />
      {overlay ? <div className="xgs-leaflet-map__overlay">{overlay}</div> : null}
    </div>
  );
};

export default XGSLeafletMap;
